import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CircleCheckIcons,
  CircleXIcons,
  InfoIcons,
  UploadIcons,
} from '../../../../../icons/icons';
import {
  DocumentConfiguration,
  IDocumentRequirementItem,
} from '../../../../../utils/types/JobOpenings';

interface DocumentChecklistProps {
  configurations?: DocumentConfiguration[];
  documentRequirements?: IDocumentRequirementItem[];
  selectedFiles: { [type: string]: File[] };
  onFileSelect: (type: string, file: File | null) => void;
  onBatchFileSelect?: (updates: { [type: string]: File | null }) => void;
  onFileRemoveAtIndex?: (type: string, index: number) => void;
  disabled?: boolean;
}

export const DocumentChecklist: React.FC<DocumentChecklistProps> = ({
  configurations,
  documentRequirements,
  selectedFiles,
  onFileSelect,
  onBatchFileSelect,
  onFileRemoveAtIndex,
  disabled = false,
}) => {
  // Build items list
  const rawItems = useMemo(
    () =>
      documentRequirements
        ? documentRequirements.map((req: IDocumentRequirementItem) => ({
          type: req.label,
          key: req.key || req.label,
          requirement: req.isMandatory ? 'Mandatory' : 'Optional',
          description: req.description || `Please upload your ${req.label}`,
          allowMultiple: req.allowMultiple ?? false,
        }))
        : configurations
          ?.filter((c: DocumentConfiguration) => c.requirement !== 'Disabled')
          .map((c: DocumentConfiguration) => ({
            type: c.type,
            key: c.type,
            requirement: c.requirement,
            description: c.description,
            allowMultiple: c.allowMultiple ?? false,
          })) || [],
    [documentRequirements, configurations],
  );

  // Sort: Mandatory first
  const activeItems = useMemo(
    () =>
      [...rawItems].sort((a, b) => {
        if (a.requirement === 'Mandatory' && b.requirement !== 'Mandatory')
          return -1;
        if (a.requirement !== 'Mandatory' && b.requirement === 'Mandatory')
          return 1;
        return 0;
      }),
    [rawItems],
  );

  // Count total uploaded files (across all types)
  const attachedCount = useMemo(
    () => Object.keys(selectedFiles).length,
    [selectedFiles],
  );

  const firstPendingMandatoryIndex = useMemo(
    () =>
      activeItems.findIndex(
        (item) =>
          item.requirement === 'Mandatory' &&
          !(selectedFiles[item.key]?.length > 0),
      ),
    [activeItems, selectedFiles],
  );

  const hasPendingMandatory = firstPendingMandatoryIndex !== -1;

  const [focusedType, setFocusedType] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (hasPendingMandatory) {
      setFocusedType(activeItems[firstPendingMandatoryIndex].key);
    }
  }, [hasPendingMandatory, firstPendingMandatoryIndex, activeItems]);

  const getIsLocked = (_itemKey: string, index: number) => {
    const item = activeItems[index];
    if (item.requirement === 'Mandatory') {
      return hasPendingMandatory && index > firstPendingMandatoryIndex;
    }
    return hasPendingMandatory;
  };

  // Effect to trigger file picker when focusedType is set (for card clicks)
  useEffect(() => {
    if (focusedType && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [focusedType]);

  const processFiles = (files: FileList | null) => {
    if (!files || files.length === 0 || disabled) return;

    if (focusedType) {
      const item = activeItems.find((i) => i.key === focusedType);
      if (item?.allowMultiple) {
        // Add ALL selected files to the multi-slot.
        Array.from(files).forEach((file) => onFileSelect(focusedType, file));
      } else {
        // Just the first file for single-slots.
        onFileSelect(focusedType, files[0]);
      }
    } else if (onBatchFileSelect) {
      const availableItems = activeItems.filter(
        (item, idx) =>
          (item.allowMultiple || !(selectedFiles[item.key]?.length > 0)) &&
          !getIsLocked(item.key, idx),
      );
      if (availableItems.length > 0) {
        const updates: { [type: string]: File | null } = {};
        const fileArray = Array.from(files);
        fileArray.forEach((file, index) => {
          // If we have more files than available slots, the extra files are ignored
          // (or could be added to multi-slots if we logic for that, but keeping it simple for now)
          if (index < availableItems.length) {
            const targetKey = availableItems[index].key;
            updates[targetKey] = file;
          }
        });
        if (Object.keys(updates).length > 0) {
          onBatchFileSelect(updates);
        }
      }
    }
    setFocusedType('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    processFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();

  const allDone =
    activeItems.length > 0 &&
    activeItems.every((item) => selectedFiles[item.key]?.length > 0);

  return (
    <div className='flex flex-col gap-5 w-full'>
      { /* ── Compact Upload Strip ── */ }
      <div
          className={`relative flex items-center gap-4 px-5 py-4 rounded-2xl border-2 border-dashed transition-all duration-300 ${
            disabled
              ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
              : allDone
                ? 'bg-emerald-50 border-emerald-300'
                : 'bg-emerald-50/40 border-emerald-300 hover:border-emerald-500 cursor-pointer group'
          }`}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
      >
        <input
            type='file'
            ref={fileInputRef}
            multiple
            className='absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed z-20 w-full h-full'
            onChange={handleFileChange}
            disabled={disabled}
        />

        <div
            className={`p-3 rounded-xl flex-shrink-0 transition-transform duration-300 group-hover:scale-110 ${
              allDone
                ? 'bg-emerald-500 text-white'
                : 'bg-emerald-100 text-emerald-600'
            }`}
        >
          <UploadIcons className='w-5 h-5' />
        </div>

        <div className='flex-1 min-w-0'>
          <p className='text-sm font-bold text-gray-900'>
            { allDone
              ? 'All documents attached!'
              : 'Drop files here or click to upload' }
          </p>
          <p className='text-xs text-gray-500 mt-0.5'>
            { allDone
              ? 'You can still add more files to multi-upload slots below'
              : focusedType
                ? `Uploading next: ${
                  activeItems.find((i) => i.key === focusedType)?.type ||
                  focusedType
                }`
                : 'Select multiple files at once to auto-assign them' }
          </p>
        </div>

        <div
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border ${
              allDone
                ? 'bg-emerald-500 text-white border-emerald-500'
                : 'bg-white text-emerald-600 border-emerald-200'
            }`}
        >
          { attachedCount } / { activeItems.length }
        </div>
      </div>

      { /* ── Document Card Grid ── */ }
      { activeItems.length > 0 && (
        <div>
          <p className='text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3'>
            Required Documents
          </p>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'>
            { activeItems.map((item, index) => {
              const uploadedFiles = selectedFiles[item.key] || [];
              const isUploaded = uploadedFiles.length > 0;
              const isLocked = getIsLocked(item.key, index);
              const isCurrent =
                (item.requirement === 'Mandatory' &&
                  index === firstPendingMandatoryIndex) ||
                (!hasPendingMandatory &&
                  !isUploaded &&
                  focusedType === item.key);
              const isFocused = focusedType === item.key;

              return (
                <div
                    key={item.key}
                    onClick={() => {
                      if (
                        !isLocked &&
                      (!isUploaded || item.allowMultiple) &&
                      !disabled
                      ) {
                        setFocusedType(item.key);
                      }
                    }}
                    className={`relative flex flex-col gap-2 p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                      isLocked || (isUploaded && !item.allowMultiple)
                        ? 'cursor-default'
                        : 'cursor-pointer'
                    } ${
                      isUploaded
                        ? 'border-blue-200 bg-blue-50/60'
                        : isFocused && !isUploaded
                          ? 'border-emerald-500 bg-emerald-50 shadow-md shadow-emerald-100'
                          : isLocked
                            ? 'border-gray-100 bg-gray-50 opacity-50'
                            : 'border-gray-200 bg-white hover:border-emerald-300 hover:shadow-sm'
                    }`}
                >
                  { /* Status indicator strip */ }
                  <div
                      className={`absolute top-0 left-0 w-full h-1 rounded-t-2xl transition-all duration-300 ${
                        isUploaded
                          ? 'bg-emerald-400'
                          : isCurrent
                            ? 'bg-emerald-300 animate-pulse'
                            : isLocked
                              ? 'bg-gray-200'
                              : 'bg-transparent'
                      }`}
                  />

                  { /* Header row */ }
                  <div className='flex items-center justify-between gap-2 mt-1'>
                    <div
                        className={`p-1.5 rounded-lg flex-shrink-0 ${
                          isUploaded
                            ? 'bg-emerald-500 text-white'
                            : isLocked
                              ? 'bg-gray-200 text-gray-400'
                              : isCurrent
                                ? 'bg-emerald-100 text-emerald-600 animate-pulse'
                                : 'bg-gray-100 text-gray-400'
                        }`}
                    >
                      { isUploaded ? (
                        <CircleCheckIcons className='w-4 h-4' />
                      ) : (
                        <InfoIcons className='w-4 h-4' />
                      ) }
                    </div>

                    <div className='flex gap-1 items-center flex-wrap flex-shrink-0'>
                      { isUploaded && (
                        <span className='text-[8px] font-black bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded border border-emerald-100 uppercase tracking-wide'>
                          File Attached
                        </span>
                      ) }
                      { item.requirement === 'Mandatory' && !isUploaded && (
                        <span className='text-[8px] font-black bg-red-50 text-red-500 px-1.5 py-0.5 rounded border border-red-100 uppercase tracking-wide px-2'>
                          Required
                        </span>
                      ) }
                      { item.requirement === 'Optional' && !isUploaded && (
                        <span className='text-[8px] font-black bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded border border-blue-100 uppercase tracking-wide'>
                          Optional
                        </span>
                      ) }
                      { /* ★ allowMultiple badge */ }
                      { item.allowMultiple && (
                        <span className='text-[8px] font-black bg-purple-50 text-purple-500 px-1.5 py-0.5 rounded border border-purple-100 uppercase tracking-wide'>
                          Multi
                        </span>
                      ) }
                      { isLocked && (
                        <span className='text-[8px] font-bold text-gray-400 uppercase tracking-widest'>
                          Locked
                        </span>
                      ) }
                    </div>
                  </div>

                  { /* Title */ }
                  <p
                      className={`text-sm font-bold leading-tight ${
                        isUploaded
                          ? 'text-blue-800'
                          : isLocked
                            ? 'text-gray-400'
                            : 'text-gray-800'
                      }`}
                  >
                    { item.type }
                  </p>

                  { /* Sub-text or uploaded file chips */ }
                  { isUploaded ? (
                    <div className='flex flex-col gap-1'>
                      { uploadedFiles.map((f, fi) => (
                        <div
                            key={fi}
                            className='flex items-center justify-between gap-1 bg-white rounded-lg px-2 py-1 border border-blue-100'
                            onClick={(e) => e.stopPropagation()}
                        >
                          <p className='text-[10px] text-blue-700 font-semibold truncate leading-none'>
                            { f.name }
                          </p>
                          { onFileRemoveAtIndex && !disabled && (
                            <button
                                type='button'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onFileRemoveAtIndex(item.key, fi);
                                }}
                                className='text-gray-300 hover:text-red-500 transition-colors flex-shrink-0'
                                title='Remove this file'
                            >
                              <CircleXIcons className='w-3.5 h-3.5' />
                            </button>
                          ) }
                        </div>
                      )) }
                      { /* If allowMultiple, show "add more" button */ }
                      { item.allowMultiple && !disabled && (
                        <button
                            type='button'
                            onClick={(e) => {
                              e.stopPropagation();
                              setFocusedType(item.key);
                            }}
                            className='text-[9px] text-purple-500 font-bold uppercase tracking-wide hover:text-purple-700 text-left mt-0.5'
                        >
                          + Add another file
                        </button>
                      ) }
                    </div>
                  ) : (
                    <p className='text-[10px] text-gray-400 leading-snug'>
                      { isLocked
                        ? 'Upload mandatory docs first'
                        : item.allowMultiple
                          ? `You can upload multiple ${item.type} files`
                          : item.description || `Upload your ${item.type}` }
                    </p>
                  ) }
                </div>
              );
            }) }
          </div>
        </div>
      ) }

      { activeItems.length === 0 && (
        <div className='p-8 text-center rounded-2xl border border-dashed border-gray-200 bg-gray-50'>
          <p className='text-sm text-gray-400'>
            No specific document requirements.
          </p>
        </div>
      ) }
    </div>
  );
};
